import { ScheduleSessionSurface } from "@/components/schedule/ScheduleSessionSurface";
import React, { useEffect, useRef } from 'react';
import { format, type Locale } from 'date-fns';
import { fr } from 'date-fns/locale';
import { useTranslation } from 'react-i18next';
import { Check, Users, CalendarX2, Settings2 } from 'lucide-react';
import { cn } from '@/lib/utils';
import { ScheduledClass } from '@/types/types';
import { resolveCalendarColor, readableTextColor, softCalendarBg } from '@/lib/calendarColors';

interface WeeklyAgendaListProps {
  weekDates: Date[];
  classes: ScheduledClass[];
  onClassClick: (classInfo: ScheduledClass, date: Date) => void;
  isClassBooked?: (classId: string, date: Date, time: string) => boolean;
  recentlyBookedSlots?: Set<string>;
  locale?: Locale;
  emptyLabel?: string;
  /**
   * Mode gestion (admin/coach) : les cartes restent cliquables même complètes
   * — un cours plein est précisément celui qu'un admin doit pouvoir ouvrir
   * (liste des présents, capacité, suppression) — et le bouton d'action devient
   * « Gérer » au lieu de « Réserver ».
   */
  manageMode?: boolean;
}

/** Places restantes, en s'appuyant sur les champs disponibles du cours. */
function spotsLeft(c: ScheduledClass): number {
  if (typeof c.spots_available === 'number') return c.spots_available;
  if (typeof c.booked_count === 'number') return (c.capacity || 0) - c.booked_count;
  return c.capacity || 0;
}

/**
 * Vue AGENDA (liste) du planning, complémentaire de la grille hebdomadaire.
 * Une carte par cours, colorée par la couleur de son planning : filet à gauche,
 * badge, bouton de réservation. Le clic ouvre le même dialogue de réservation
 * que la grille (`onClassClick`) — toute la logique métier (quota, calendrier
 * autorisé) reste côté dialogue.
 */
export const WeeklyAgendaList: React.FC<WeeklyAgendaListProps> = ({
  weekDates,
  classes,
  onClassClick,
  isClassBooked,
  recentlyBookedSlots,
  locale = fr,
  emptyLabel,
  manageMode = false,
}) => {
  const { t } = useTranslation(['schedule']);
  const emptyText = emptyLabel ?? t('schedule:noClassesThisWeek', 'Aucun cours cette semaine');
  const byDay = weekDates.map((date) => {
    const key = format(date, 'yyyy-MM-dd');
    const dayClasses = (classes || [])
      .filter((c) => c.class_date === key)
      .sort((a, b) => (a.time || '').localeCompare(b.time || ''));
    return { date, key, dayClasses };
  });

  const total = byDay.reduce((n, d) => n + d.dayClasses.length, 0);

  // Auto-positionnement sur le créneau du moment : quand la semaine affichée
  // contient AUJOURD'HUI, on défile jusqu'au créneau immédiatement INFÉRIEUR
  // à l'heure courante (ex. à 12:40 → le cours de 12:30) — c'est le cours en
  // cours ou tout juste passé, le point de repère naturel. S'il n'y a encore
  // rien eu aujourd'hui, on vise le premier cours du jour ; si le jour est
  // vide, le premier jour à venir. Une seule fois par semaine affichée : la
  // navigation manuelle reprend ensuite la main.
  const todayKey = format(new Date(), 'yyyy-MM-dd');
  const nowHM = format(new Date(), 'HH:mm');
  const daysWithClasses = byDay.filter((d) => d.dayClasses.length > 0);
  // Cible : premier jour ≥ aujourd'hui — et à défaut le DERNIER jour passé.
  // Sans ce repli, un jour sans cours en fin de semaine (ex. dimanche) ne
  // produisait AUCUNE cible → aucun scroll, alors que le repère naturel est
  // la fin de la semaine écoulée.
  const targetDay =
    daysWithClasses.find((d) => d.key >= todayKey) ??
    daysWithClasses[daysWithClasses.length - 1];
  let targetSlotKey: string | null = null;
  if (targetDay) {
    let target = targetDay.dayClasses[0];
    if (targetDay.key === todayKey) {
      const started = targetDay.dayClasses.filter((c) => (c.time || '') <= nowHM);
      if (started.length > 0) target = started[started.length - 1];
    } else if (targetDay.key < todayKey) {
      // Jour passé (repli) : dernier cours de la journée.
      target = targetDay.dayClasses[targetDay.dayClasses.length - 1];
    }
    targetSlotKey = `${target.id}-${targetDay.key}-${target.time}`;
  }

  const weekSignature = weekDates.map((d) => format(d, 'yyyy-MM-dd')).join('|');
  const scrolledForWeekRef = useRef<string | null>(null);
  const targetElRef = useRef<HTMLElement | null>(null);

  useEffect(() => {
    if (!targetSlotKey) return;
    if (scrolledForWeekRef.current === weekSignature) return;
    const el = targetElRef.current;
    if (!el) return;
    scrolledForWeekRef.current = weekSignature;
    // Double tentative : la première peut être annulée par un layout tardif
    // (cartes qui finissent de se monter) — la seconde part d'une mise en
    // page stabilisée.
    requestAnimationFrame(() => {
      el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });
    const retry = setTimeout(() => {
      targetElRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 350);
    return () => clearTimeout(retry);
    // total en dépendance : les cours arrivent en async, la cible n'existe
    // qu'une fois la liste réellement rendue.
  }, [weekSignature, targetSlotKey, total]);

  if (total === 0) {
    return (
      <div className="flex flex-col items-center justify-center gap-3 rounded-2xl border border-dashed border-border py-16 text-center">
        <CalendarX2 className="h-8 w-8 text-muted-foreground/60" />
        <p className="text-sm text-muted-foreground">{emptyText}</p>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-6">
      {byDay
        .filter((d) => d.dayClasses.length > 0)
        .map(({ date, key, dayClasses }) => (
          <section key={key} className="flex flex-col gap-2.5">
            <h3 className="px-1 text-xs font-bold uppercase tracking-wider text-muted-foreground/80">
              {format(date, 'EEEE d MMMM', { locale })}
            </h3>

            {dayClasses.map((c) => {
              const color = resolveCalendarColor(c.calendar_color, c.calendar_id);
              const spots = spotsLeft(c);
              const isFull = spots <= 0;
              const remainingPercent = c.capacity ? (spots / c.capacity) * 100 : 0;
              const capacityTone = isFull ? 'full' : remainingPercent >= 60 ? 'available' : remainingPercent >= 40 ? 'limited' : 'nearly-full';
              const slotKey = `${c.id}-${key}-${c.time}`;
              const booked =
                (isClassBooked?.(c.id, date, c.time) ?? false) ||
                (recentlyBookedSlots?.has(slotKey) ?? false);
              const occupied = Math.max(0, (c.capacity || 0) - spots);
              // Adhérent : places restantes/total ; admin/coach : réservés/total.
              const pct = c.capacity ? Math.min(100, Math.round(((manageMode ? occupied : spots) / c.capacity) * 100)) : 0;
              const capState = isFull ? 'full' : pct >= 85 ? 'warn' : 'ok';

              // En mode gestion, la carte reste TOUJOURS cliquable : « complet »
              // ou « déjà réservé » ne concernent que la réservation adhérent.
              const clickable = manageMode || !(booked || isFull);

              return (
                <ScheduleSessionSurface key={c.id}>
                <article
                  data-capacity-tone={capacityTone}
                  ref={
                    slotKey === targetSlotKey
                      ? (el) => { targetElRef.current = el; }
                      : undefined
                  }
                  onClick={clickable ? () => onClassClick(c, date) : undefined}
                  className={cn(
                    // scroll-mt : marge au scrollIntoView pour ne pas coller la
                    // carte ciblée sous un éventuel en-tête collant.
                    'group relative grid scroll-mt-24 grid-cols-[54px_1fr_auto] items-center gap-3 overflow-hidden rounded-2xl border p-3 pl-4 shadow-sm transition-all',
                    booked ? 'schedule-capacity-booked' : `schedule-capacity-${capacityTone}`,
                    clickable
                      ? 'cursor-pointer hover:-translate-y-px hover:shadow-md'
                      : 'cursor-default'
                  )}
                >
                  {/* Filet coloré du planning */}


                  {/* Heure */}
                  <div className="text-center">
                    <div className="text-[15px] font-bold tabular-nums leading-none tracking-tight">
                      {c.time}
                    </div>
                    {c.duration && (
                      <div className="mt-1 text-[11px] tabular-nums text-muted-foreground">
                        {c.duration} min
                      </div>
                    )}
                  </div>

                  {/* Infos */}
                  <div className="min-w-0">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="truncate text-[15px] font-semibold leading-tight">{c.name}</span>
                      {c.calendar_name && (
                        <span
                          className="rounded-full px-2 py-0.5 text-[11px] font-bold"
                          style={{ color, backgroundColor: softCalendarBg(color) }}
                        >
                          {c.calendar_name}
                        </span>
                      )}
                    </div>
                    <div className="mt-1 flex flex-wrap items-center gap-x-3.5 gap-y-1 text-[13px] text-muted-foreground">
                      {c.instructor && <span className="truncate">{c.instructor}</span>}
                      <span className="inline-flex items-center gap-1.5">
                        <Users className="h-3.5 w-3.5" />
                        <span className="inline-block h-1.5 w-12 overflow-hidden rounded-full bg-muted align-middle">
                          <span
                            className={cn(
                              'block h-full rounded-full',
                              capState === 'ok' && 'bg-[hsl(var(--schedule-available))]',
                              capState === 'warn' && 'bg-[hsl(var(--schedule-limited))]',
                              capState === 'full' && 'bg-[hsl(var(--schedule-full))]'
                            )}
                            style={{ width: `${pct}%` }}
                          />
                        </span>
                        <span className="tabular-nums">
                          {manageMode ? `${occupied}/${c.capacity}` : isFull ? t('schedule:full', 'Complet') : `${spots}`}
                        </span>
                      </span>
                    </div>
                  </div>

                  {/* Action */}
                  <div className="flex justify-end">
                    {manageMode ? (
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          onClassClick(c, date);
                        }}
                        className="inline-flex items-center gap-1.5 rounded-lg px-4 py-2 text-[13.5px] font-semibold shadow-sm transition-[filter,transform] hover:-translate-y-px hover:brightness-105"
                        style={{ backgroundColor: color, color: readableTextColor(color) }}
                      >
                        <Settings2 className="h-3.5 w-3.5" />
                        {t('schedule:manage', 'Gérer')}
                      </button>
                    ) : booked ? (
                      <span className="inline-flex items-center gap-1.5 rounded-full bg-[hsl(var(--schedule-booked,var(--schedule-available))/0.14)] px-3 py-1.5 text-[13px] font-bold text-[hsl(var(--schedule-booked,var(--schedule-available)))]">
                        <Check className="h-3.5 w-3.5" /> {t('schedule:bookedLabel', 'Réservé')}
                      </span>
                    ) : isFull ? (
                      <span className="rounded-lg bg-red-500/10 px-3.5 py-2 text-[13px] font-semibold text-red-500">
                        {t('schedule:full', 'Complet')}
                      </span>
                    ) : (
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          onClassClick(c, date);
                        }}
                        className="rounded-lg px-4 py-2 text-[13.5px] font-semibold text-white shadow-sm transition-[filter,transform] hover:-translate-y-px hover:brightness-105"
                        style={{ backgroundColor: color, color: readableTextColor(color) }}
                      >
                        {t('schedule:book', 'Réserver')}
                      </button>
                    )}
                  </div>
                </article>
                </ScheduleSessionSurface>
              );
            })}
          </section>
        ))}
    </div>
  );
};
